(() => {
  const root = document.querySelector('[data-rokr-quiz-root]');
  if (!root) return;

  const dialog = root.querySelector('.rokr-quiz__dialog');
  const content = root.querySelector('[data-rokr-quiz-content]');
  const progress = root.querySelector('[data-rokr-quiz-progress]');
  const stepLabel = root.querySelector('[data-rokr-quiz-step-label]');
  const nextBtn = root.querySelector('[data-rokr-quiz-next]');
  const backBtn = root.querySelector('[data-rokr-quiz-back]');
  const actions = root.querySelector('[data-rokr-quiz-actions]');
  const status = root.querySelector('[data-rokr-quiz-status]');
  const endpoint = root.dataset.endpoint;
  const currency = root.dataset.currency || 'EUR';

  let products = [];
  let currentStep = 0;
  let lastFocused = null;
  let isLoaded = false;
  const answers = {};

  const normalize = (value) => String(value ?? '').trim();
  const normLower = (value) => normalize(value).toLocaleLowerCase('fr-FR');

  function normalizeList(value) {
    if (Array.isArray(value)) return value.map(normalize).filter(Boolean);
    const text = normalize(value);
    if (!text) return [];
    return text.split(/\s*[|,;]\s*/).map(normalize).filter(Boolean);
  }

  function parseBuildHours(value) {
    const text = normLower(value).replace(',', '.');
    if (!text) return null;

    const range = text.match(/(\d+(?:\.\d+)?)\s*(?:-|–|à)\s*(\d+(?:\.\d+)?)\s*h/);
    if (range) return (Number(range[1]) + Number(range[2])) / 2;

    const hm = text.match(/(\d+(?:\.\d+)?)\s*h(?:eures?)?\s*(\d{1,2})?/);
    if (hm) return Number(hm[1]) + (hm[2] ? Number(hm[2]) / 60 : 0);

    const minutes = text.match(/(\d+(?:\.\d+)?)\s*min/);
    if (minutes) return Number(minutes[1]) / 60;

    const number = text.match(/\d+(?:\.\d+)?/);
    return number ? Number(number[0]) : null;
  }

  function parseAge(value) {
    const n = normalize(value).match(/\d+/);
    return n ? Number(n[0]) : null;
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  async function loadProducts() {
    if (isLoaded) return;
    showStatus('Chargement des modèles…');

    let page = 1;
    let keepGoing = true;
    const all = [];

    while (keepGoing && page <= 10) {
      const separator = endpoint.includes('?') ? '&' : '?';
      const response = await fetch(`${endpoint}${separator}page=${page}`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data.products)) all.push(...data.products);
      keepGoing = Boolean(data.next_page);
      page = data.next_page || page + 1;
    }

    products = all.filter(p => p && p.handle && p.handle !== 'link-pack');
    isLoaded = products.length > 0;
    hideStatus();
    if (!isLoaded) throw new Error('Aucun produit éligible trouvé.');
  }

  function showStatus(message) {
    status.hidden = false;
    status.textContent = message;
  }
  function hideStatus() {
    status.hidden = true;
    status.textContent = '';
  }

  function getUniverses() {
    const ignored = new Set(['tous nos modèles', 'tous les modèles', 'all products']);
    const values = new Set();
    products.forEach(product => {
      normalizeList(product.universe).forEach(item => {
        if (!ignored.has(normLower(item))) values.add(item);
      });
    });
    return [...values].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }

  function getQuestions() {
    return [
      {
        id: 'recipient',
        title: 'Pour qui recherchez-vous ce modèle ?',
        hint: 'Cela nous aide à ajuster le type de recommandation.',
        options: [
          ['self', 'Pour moi', 'Je cherche le modèle qui me correspond le mieux.'],
          ['gift', 'Pour offrir', 'Je veux maximiser l’effet cadeau.'],
          ['family', 'À construire en famille', 'Je cherche une expérience accessible à partager.']
        ]
      },
      {
        id: 'universe',
        title: 'Quels univers vous attirent le plus ?',
        hint: 'Sélectionnez jusqu’à 3 univers. Ils sont générés automatiquement depuis les modèles actuellement proposés.',
        multi: true,
        maxSelections: 3,
        options: [...getUniverses().map(v => [v, v, '']), ['__any__', 'Surprenez-moi', 'Je préfère laisser le quiz choisir.']]
      },
      {
        id: 'difficulty',
        title: 'Quel niveau de difficulté recherchez-vous ?',
        options: [
          ['Débutant', 'Débutant', 'Accessible et rassurant.'],
          ['Intermédiaire', 'Intermédiaire', 'Un bon équilibre entre plaisir et défi.'],
          ['Avancé', 'Avancé', 'Je veux une construction exigeante.'],
          ['__any__', 'Peu importe', 'Le niveau n’est pas un critère pour moi.']
        ]
      },
      {
        id: 'time',
        title: 'Combien de temps souhaitez-vous consacrer au montage ?',
        options: [
          ['short', 'Une petite session', 'Jusqu’à environ 2 heures.'],
          ['medium', 'Quelques heures', 'Environ 2 h 30 à 4 heures.'],
          ['project', 'Un beau projet', 'Environ 4 h 30 à 6 heures.'],
          ['long', 'Je veux prendre mon temps', 'Plus de 6 heures.'],
          ['__any__', 'Peu importe', 'La durée n’est pas un critère.']
        ]
      },
      {
        id: 'feature',
        title: 'Qu’est-ce qui vous attire le plus dans votre futur modèle ?',
        hint: 'Sélectionnez jusqu’à 3 réponses.',
        multi: true,
        maxSelections: 3,
        options: [
          ['mechanisms', 'Voir les mécanismes fonctionner', 'Des pièces mobiles et un vrai plaisir mécanique.'],
          ['lighting', 'Une ambiance lumineuse', 'Je veux un modèle qui s’illumine.'],
          ['music', 'Un modèle musical', 'La musique fait partie de l’expérience.'],
          ['motorized', 'Un modèle motorisé', 'Je veux un modèle animé par un moteur.'],
          ['display', 'Une belle pièce à exposer', 'Le rendu décoratif est prioritaire.'],
          ['__any__', 'Peu importe', 'Je suis ouvert à toutes les expériences.']
        ]
      },
      {
        id: 'budget',
        title: 'Quel budget souhaitez-vous consacrer à votre modèle ?',
        options: [
          ['under45', 'Moins de 45 €', ''],
          ['45to55', '45 à 55 €', ''],
          ['55to70', '55 à 70 €', ''],
          ['over70', 'Plus de 70 €', ''],
          ['__any__', 'Le budget n’est pas important', '']
        ]
      }
    ];
  }

  function hasAnswer(question) {
    const value = answers[question.id];
    return question.multi ? Array.isArray(value) && value.length > 0 : Boolean(value);
  }

  function renderStep() {
    const questions = getQuestions();
    const q = questions[currentStep];
    const selected = q.multi
      ? (Array.isArray(answers[q.id]) ? answers[q.id] : [])
      : answers[q.id];

    progress.style.width = `${((currentStep + 1) / questions.length) * 100}%`;
    stepLabel.textContent = `Étape ${currentStep + 1} sur ${questions.length}`;
    backBtn.disabled = currentStep === 0;
    nextBtn.disabled = !hasAnswer(q);
    nextBtn.textContent = currentStep === questions.length - 1 ? 'Voir mes recommandations' : 'Continuer';
    actions.hidden = false;
    actions.style.display = '';

    const choices = q.options.map(([value, label, desc]) => {
      const isSelected = q.multi ? selected.includes(value) : selected === value;
      return `
        <button
          type="button"
          class="rokr-quiz__choice${isSelected ? ' is-selected' : ''}"
          data-value="${escapeAttr(value)}"
          aria-pressed="${isSelected ? 'true' : 'false'}"
        >
          <span class="rokr-quiz__choice-check" aria-hidden="true">
            <svg viewBox="0 0 20 20" focusable="false"><path d="M5 10.2l3 3L15.2 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <strong>${escapeHtml(label)}</strong>
          ${desc ? `<span>${escapeHtml(desc)}</span>` : ''}
        </button>
      `;
    }).join('');

    const selectionInfo = q.multi
      ? `<p class="rokr-quiz__selection-info" data-rokr-quiz-selection-info>${selected.includes('__any__') ? 'Aucune préférence particulière' : `${selected.length} / ${q.maxSelections} sélection${selected.length > 1 ? 's' : ''}`}</p>`
      : '';

    content.innerHTML = `
      <h3 class="rokr-quiz__question">${escapeHtml(q.title)}</h3>
      ${q.hint ? `<p class="rokr-quiz__hint">${escapeHtml(q.hint)}</p>` : ''}
      ${selectionInfo}
      <div class="rokr-quiz__choices">${choices}</div>
    `;

    content.querySelectorAll('[data-value]').forEach(btn => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.value;

        if (!q.multi) {
          answers[q.id] = value;
        } else {
          let values = Array.isArray(answers[q.id]) ? [...answers[q.id]] : [];

          if (value === '__any__') {
            values = values.includes('__any__') ? [] : ['__any__'];
          } else {
            values = values.filter(item => item !== '__any__');
            if (values.includes(value)) {
              values = values.filter(item => item !== value);
            } else if (values.length < q.maxSelections) {
              values.push(value);
            }
          }

          answers[q.id] = values;
        }

        renderStep();
      });
    });
  }

  function scoreProduct(product) {
    let score = 0;
    const reasons = [];

    // Univers — 30 points maximum, normalisés si plusieurs univers sont choisis.
    const selectedUniverses = Array.isArray(answers.universe) ? answers.universe.filter(v => v !== '__any__') : [];
    if (selectedUniverses.length) {
      const universes = normalizeList(product.universe).map(normLower);
      const matches = selectedUniverses.filter(value => universes.includes(normLower(value)));
      if (matches.length) {
        score += 30 * (matches.length / selectedUniverses.length);
        reasons.push(matches.length > 1 ? `ses univers ${matches.join(' et ')}` : `son univers ${matches[0]}`);
      }
    }

    // Difficulté — 20 points
    if (answers.difficulty && answers.difficulty !== '__any__') {
      const d = normLower(product.difficulty);
      const wanted = normLower(answers.difficulty);
      if (d === wanted) { score += 20; reasons.push(`son niveau ${answers.difficulty.toLowerCase()}`); }
      else if ((wanted === 'intermédiaire' && d === 'débutant') || (wanted === 'débutant' && d === 'intermédiaire')) score += 8;
      else if ((wanted === 'intermédiaire' && d === 'avancé') || (wanted === 'avancé' && d === 'intermédiaire')) score += 6;
      else score += 2;
    }

    // Temps — 15 points
    const hours = parseBuildHours(product.buildTime);
    if (answers.time && answers.time !== '__any__' && hours !== null) {
      const exact = (
        (answers.time === 'short' && hours <= 2) ||
        (answers.time === 'medium' && hours > 2 && hours <= 4) ||
        (answers.time === 'project' && hours > 4 && hours <= 6) ||
        (answers.time === 'long' && hours > 6)
      );
      if (exact) { score += 15; reasons.push('un temps de montage adapté'); }
      else {
        const bucket = hours <= 2 ? 0 : hours <= 4 ? 1 : hours <= 6 ? 2 : 3;
        const target = { short: 0, medium: 1, project: 2, long: 3 }[answers.time];
        const distance = Math.abs(bucket - target);
        if (distance === 1) score += 7;
        else if (distance === 2) score += 3;
      }
    }

    // Préférences — 20 points maximum, normalisés si plusieurs réponses sont choisies.
    const selectedFeatures = Array.isArray(answers.feature) ? answers.feature.filter(v => v !== '__any__') : [];
    if (selectedFeatures.length) {
      let featureMatchTotal = 0;
      const featureReasons = [];

      selectedFeatures.forEach(feature => {
        if (feature === 'mechanisms' && product.mechanisms === true) { featureMatchTotal += 1; featureReasons.push('ses mécanismes fonctionnels'); }
        if (feature === 'lighting' && product.lighting === true) { featureMatchTotal += 1; featureReasons.push('son éclairage'); }
        if (feature === 'music' && product.music === true) { featureMatchTotal += 1; featureReasons.push('son univers musical'); }
        if (feature === 'motorized' && product.motorized === true) { featureMatchTotal += 1; featureReasons.push('sa motorisation'); }
        if (feature === 'display') {
          const display = normLower(product.display);
          if (display === 'élevé' || display === 'eleve') { featureMatchTotal += 1; featureReasons.push('son fort potentiel d’exposition'); }
          else if (display === 'moyen') { featureMatchTotal += 0.5; featureReasons.push('son joli rendu à exposer'); }
        }
      });

      score += 20 * (featureMatchTotal / selectedFeatures.length);
      reasons.push(...featureReasons);
    }

    // Budget — 10 points, avec tolérance
    if (answers.budget && answers.budget !== '__any__') {
      const euros = (Number(product.price) || 0) / 100;
      let exact = false;
      let distance = 999;
      if (answers.budget === 'under45') { exact = euros < 45; distance = Math.max(0, euros - 45); }
      if (answers.budget === '45to55') { exact = euros >= 45 && euros <= 55; distance = euros < 45 ? 45 - euros : Math.max(0, euros - 55); }
      if (answers.budget === '55to70') { exact = euros >= 55 && euros <= 70; distance = euros < 55 ? 55 - euros : Math.max(0, euros - 70); }
      if (answers.budget === 'over70') { exact = euros > 70; distance = Math.max(0, 70 - euros); }
      if (exact) { score += 10; reasons.push('son prix dans votre budget'); }
      else if (distance <= 5) score += 6;
      else if (distance <= 10) score += 3;
    }

    // Pour qui ? — 5 points
    if (answers.recipient === 'gift') {
      const gift = normLower(product.gift);
      if (gift === 'élevé' || gift === 'eleve') { score += 5; reasons.push('son excellent potentiel cadeau'); }
      else if (gift === 'moyen') score += 3;
    }

    if (answers.recipient === 'family') {
      const age = parseAge(product.age);
      const diff = normLower(product.difficulty);
      if ((age !== null && age <= 14) && (diff === 'débutant' || diff === 'intermédiaire')) { score += 5; reasons.push('son accessibilité pour un montage partagé'); }
      else if (age !== null && age <= 14) score += 3;
      else if (diff === 'débutant' || diff === 'intermédiaire') score += 2;
    }

    if (answers.recipient === 'self') {
      const profiles = normalizeList(product.profile).map(normLower);
      const wanted = normLower(answers.difficulty);
      const good = wanted === 'débutant' ? ['débutant'] : wanted === 'intermédiaire' ? ['curieux', 'patient'] : wanted === 'avancé' ? ['passionné', 'expert'] : [];
      if (good.some(v => profiles.includes(v))) { score += 5; reasons.push('son profil de construction adapté'); }
      else if (good.length && profiles.length) score += 2;
    }

    // Petit bonus disponibilité, sans exclure les produits actifs temporairement épuisés.
    if (product.available === true) score += 1;

    return { product, score, reasons };
  }

  function renderResults() {
    const scored = products.map(scoreProduct).sort((a, b) => b.score - a.score || Number(b.product.available) - Number(a.product.available));
    const available = scored.filter(x => x.product.available === true);
    const top = (available.length >= 3 ? available : scored).slice(0, 3);

    progress.style.width = '100%';
    stepLabel.textContent = 'Vos recommandations';
    actions.hidden = true;
    actions.style.display = 'none';

    const cards = top.map((item, index) => {
      const p = item.product;
      const why = buildWhy(item);
      const kicker = index === 0 ? 'Notre meilleur choix pour vous' : index === 1 ? 'Excellente alternative' : 'Autre modèle à découvrir';
      return `
        <article class="rokr-quiz__result-card${index === 0 ? ' rokr-quiz__result-card--primary' : ''}">
          ${p.image ? `<img class="rokr-quiz__result-image" src="${escapeAttr(p.image)}" alt="${escapeAttr(p.title)}" loading="lazy">` : '<div></div>'}
          <div>
            <div class="rokr-quiz__result-kicker">${kicker}</div>
            <h4 class="rokr-quiz__result-title">${escapeHtml(p.title)}</h4>
            <div class="rokr-quiz__result-price">${money(p.price)}</div>
            <p class="rokr-quiz__result-why">${escapeHtml(why)}</p>
            <a class="rokr-quiz__result-link" href="${escapeAttr(p.url)}">Voir le modèle</a>
          </div>
        </article>
      `;
    }).join('');

    content.innerHTML = `
      <div class="rokr-quiz__results-head">
        <h3 class="rokr-quiz__results-title">Voici les modèles qui vous correspondent le mieux.</h3>
        <p class="rokr-quiz__results-subtitle">Le classement combine vos préférences avec les caractéristiques réelles de chaque modèle.</p>
      </div>
      <div class="rokr-quiz__results">${cards}</div>
      <button class="rokr-quiz__restart" type="button" data-rokr-quiz-restart>Recommencer le test</button>
    `;

    content.querySelector('[data-rokr-quiz-restart]')?.addEventListener('click', () => {
      Object.keys(answers).forEach(key => delete answers[key]);
      currentStep = 0;
      renderStep();
    });
  }

  function buildWhy(item) {
    const unique = [...new Set(item.reasons)].slice(0, 3);
    if (!unique.length) return 'Ce modèle ressort comme l’un des choix les plus équilibrés selon vos réponses.';
    if (unique.length === 1) return `Il se distingue particulièrement par ${unique[0]}.`;
    if (unique.length === 2) return `Il correspond à vos envies grâce à ${unique[0]} et ${unique[1]}.`;
    return `Il correspond à vos envies grâce à ${unique[0]}, ${unique[1]} et ${unique[2]}.`;
  }

  async function openQuiz(trigger) {
    lastFocused = trigger || document.activeElement;
    root.hidden = false;
    document.body.classList.add('rokr-quiz-open');
    dialog.focus();

    try {
      await loadProducts();
      currentStep = 0;
      renderStep();
    } catch (error) {
      console.error('[ROKR Quiz]', error);
      showStatus('Impossible de charger les modèles pour le moment. Réessayez dans quelques instants.');
      content.innerHTML = '';
      actions.hidden = true;
      actions.style.display = 'none';
    }
  }

  function closeQuiz() {
    root.hidden = true;
    document.body.classList.remove('rokr-quiz-open');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  nextBtn.addEventListener('click', (event) => {
    event.preventDefault();

    const questions = getQuestions();
    const question = questions[currentStep];

    if (!question || !hasAnswer(question)) return;

    if (currentStep >= questions.length - 1) {
      try {
        renderResults();

        // Le popup lui-même est la zone scrollable.
        // On remonte instantanément pour montrer le début des recommandations.
        dialog.scrollTop = 0;
      } catch (error) {
        console.error('[ROKR Quiz] Impossible d’afficher les recommandations :', error);
        showStatus('Une erreur est survenue lors de l’affichage des recommandations. Réessayez.');
      }

      return;
    }

    currentStep += 1;
    renderStep();
    dialog.scrollTop = 0;
  });

  backBtn.addEventListener('click', () => {
    if (currentStep > 0) { currentStep -= 1; renderStep(); }
  });

  root.querySelectorAll('[data-rokr-quiz-close]').forEach(el => el.addEventListener('click', closeQuiz));

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(
      '[data-rokr-quiz-open], a[href="#rokr-quiz"]'
    );
    if (!trigger) return;
    event.preventDefault();
    openQuiz(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') closeQuiz();
    if (event.key === 'Tab') trapFocus(event);
  });

  function trapFocus(event) {
    const focusable = [...dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].filter(el => !el.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function escapeHtml(value) {
    return normalize(value).replace(/[&<>'"]/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[c]));
  }
  function escapeAttr(value) { return escapeHtml(value); }
})();
